/**
 * Login Page - The Intelligent Standard
 *
 * A sophisticated split-screen login with the strategic color hierarchy:
 * Red hero panel for intrigue, cream for trust.
 */

import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { CheckMarkBullet, SignInFormContent } from '../components/auth';
import { Logo } from '../components/Logo';
import { useAuth } from '../contexts/AuthContext';

// Decorative curve pattern for hero panel
const HeroCurvePattern = () => (
  <svg
    className="absolute inset-0 w-full h-full opacity-20"
    viewBox="0 0 800 1000"
    preserveAspectRatio="xMidYMid slice"
    fill="none"
  >
    <path
      d="M -100 200 Q 200 100, 400 250 T 800 150"
      stroke="white"
      strokeWidth="1"
      fill="none"
      opacity="0.4"
    />
    <path
      d="M -100 400 Q 250 300, 500 450 T 900 350"
      stroke="white"
      strokeWidth="1"
      fill="none"
      opacity="0.3"
    />
    <path
      d="M -100 600 Q 300 500, 550 650 T 900 550"
      stroke="white"
      strokeWidth="1"
      fill="none"
      opacity="0.2"
    />
    <path
      d="M -100 800 Q 350 700, 600 850 T 900 750"
      stroke="white"
      strokeWidth="1"
      fill="none"
      opacity="0.1"
    />
  </svg>
);

export default function LoginPage() {
  const { user, signIn, signInWithEmail } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from =
    (location.state as { from?: { pathname: string } })?.from?.pathname || '/';

  useEffect(() => {
    if (user) {
      navigate(from, { replace: true });
    }
  }, [user, navigate, from]);

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Meridian Red Hero */}
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden flex-col justify-center px-16 py-20"
        style={{
          background: 'linear-gradient(135deg, #8B2332 0%, #6B1A27 100%)',
        }}
      >
        <HeroCurvePattern />

        <div className="relative z-10">
          <div className="mb-10">
            <Logo size={48} variant="light" />
          </div>

          <h1
            className="font-serif text-5xl font-medium mb-6 leading-tight max-w-md"
            style={{ color: '#FFFFFF' }}
          >
            Join the arena to shape the future of financial modeling
          </h1>

          <p
            className="text-lg leading-relaxed max-w-md mb-10"
            style={{ color: 'rgba(255, 255, 255, 0.8)' }}
          >
            Discover which AI builds the best spreadsheets. Your votes shape the
            future of financial modeling.
          </p>

          <div className="space-y-3">
            <CheckMarkBullet
              text="Free access to leading AI models"
              variant="light"
            />
            <CheckMarkBullet
              text="Compare their performance on practical tasks"
              variant="light"
            />
            <CheckMarkBullet text="Shape the leaderboard" variant="light" />
          </div>
        </div>
      </div>

      {/* Right Panel - Sign In */}
      <div
        className="flex-1 flex items-center justify-center p-8"
        style={{ background: '#F5F0EB' }}
      >
        <div className="w-full max-w-[400px]">
          {/* Mobile logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <Logo size={56} variant="default" />
          </div>

          <div className="text-center mb-10">
            <h2
              className="font-serif text-4xl font-medium mb-3"
              style={{ color: '#1A1A1A' }}
            >
              Sign in to start voting
            </h2>
          </div>

          {/* Sign in card */}
          <div
            className="p-8 rounded-xl"
            style={{
              background: '#FFFFFF',
              border: '1px solid #E8E0D8',
              boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
            }}
          >
            <SignInFormContent
              signIn={signIn}
              signInWithEmail={signInWithEmail}
              idPrefix="page"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
